<template>
  <div ref="lazyLoadSelectRootRef" class="lazy-load-select">
    <CustomSelect
      ref="lazyLoadCustomSelectRef"
      :deselect-from-dropdown="true"
      :append-dropdown-to-body="appendDropdownToBody"
      :append-dropdown-fixed-position="appendDropdownFixedPosition"
      :input-id="resolvedInputId"
      :accessible-label="accessibleLabel"
      :hide-floating-label="hideFloatingLabel"
      :labelled-by="labelledBy"
      v-model="state.selectedValue"
      :class="[classes, { 'ps__child--consume': state.isOpened }]"
      :selected="selected"
      :multiple="isMultiple"
      :options="selectOptions"
      :clearable="false"
      :placeholder="placeholder ? placeholder : $t('common.select')"
      :autoscroll="autoscrollEnabled"
      :close-on-select="checkForCloseSelect"
      :filterable="false"
      :reduce="reduce"
      :selectable="isOptionSelectable"
      :title="title"
      :select-only-names="selectOnlyNames"
      :invalid-feedback="invalidFeedback"
      :required="required"
      :disabled="disabled"
      @search="onSearch"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template #list-footer>
        <div v-if="showLoadingLabel" class="p-2 text-center">
          {{ $t("common.loading_more") }}
        </div>
      </template>
      <template #footer>
        <slot name="footer" />
      </template>
      <template #no-options>
        <span class="text-black-50 pm-1 d-block">
          {{ $t("common.sorry_no_matching_options") }}
        </span>
      </template>
    </CustomSelect>
    <span
      v-if="state.isProcessing"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ $t("common.loading_more") }}
    </span>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  watch,
  onMounted,
  onUnmounted,
  ref,
  getCurrentInstance,
  nextTick
} from "vue"
import { useStore } from "vuex"
import CustomSelect from "./CustomSelect.vue"

export default {
  components: { CustomSelect },
  name: "LazyLoadSelect",
  props: [
    "classes",
    "isMultiple",
    "placeholder",
    "selected",
    "type",
    "isEmailHidden",
    "reduce",
    "selectable",
    "externalOptions",
    "showInactive",
    "isInKiosk",
    "default",
    "prependItems",
    "disabled",
    "context",
    "onlyBasicData",
    "refreshOnOpen",
    "excludeOptions",
    "forceClearCache",
    "title",
    "reports",
    "invalidFeedback",
    "required",
    "closeAfterSelect",
    "selectOnlyNames",
    "addedOptions",
    "field",
    "adultPassRequestDisabled",
    "isSLP",
    "accessibleLabel",
    /** Stable id for the combobox search input (pairs with `<Label :html-for>`). */
    "inputId",
    /** Set false only if autoscroll conflicts with a custom dropdown (default: true). */
    "autoscroll",
    /**
     * When true, no visible label is rendered inside CustomSelect (use an external `<Label :html-for="inputId">`).
     * Keep `accessible-label` for programmatic names (errors, clear button); pair with `labelled-by` for `aria-labelledby`.
     */
    "hideFloatingLabel",
    /** @see CustomSelect `labelledBy` — `id` of external `<label>` (e.g. `Label` `label-id`). */
    "labelledBy",
    /**
     * When true, portals the listbox to `document.body` so it is not clipped by modal/scroll `overflow`
     * (see `CustomSelect.appendDropdownToBody`).
     */
    "appendDropdownToBody",
    /** Opt-in fixed viewport positioning with body append (see `CustomSelect.appendDropdownFixedPosition`). */
    "appendDropdownFixedPosition"
  ],
  emits: ["changed", "update:model-value"],
  setup(props, { emit }) {
    const store = useStore()

    const state = reactive({
      selectedValue: null,
      options: [],
      isOpened: false,
      params: {
        per_page: 25,
        page: 1
      },
      searchQuery: "",
      last_page: null,
      total: null,
      isProcessing: false,
      searchKeys: {
        locations: ["name"],
        teacher: ["name"],
        staff: ["name"],
        student: ["name", "email"],
        adults: ["name", "email"],
        teacher_staff: ["name", "email"],
        combined: ["name", "email"],
        appointment: ["name"],
        kiosk: ["name"],
        locations_availability: ["name"],
        my_locations: ["name"],
        frontdesk: ["name"]
      },
      processingTimeOut: null
    })

    const isLastPage = computed(() => {
      return state.last_page && state.last_page <= state.params.page
    })

    /** vue-select scrolls the list to keep the highlighted option in view; was false and broke keyboard scroll. */
    const autoscrollEnabled = computed(() => props.autoscroll !== false)

    const excludedOptionIDs = computed(() => {
      return props.excludeOptions && props.excludeOptions.length
        ? props.excludeOptions.map((el) => el.value.id)
        : null
    })

    const selectOptions = computed(() => {
      const prependOptions = props.prependItems || []
      const filteredOptions = prependOptions.filter(
        (pi) => !state.searchQuery || pi.label.includes(state.searchQuery)
      )
      const options = props.externalOptions
        ? props.externalOptions
        : state.options

      let filteredOptionsList = options

      if (excludedOptionIDs.value) {
        filteredOptionsList = filteredOptionsList.filter(
          (option) => !excludedOptionIDs.value.includes(option.value.id)
        )
      }

      if (disabledOptionIds.value && disabledOptionIds.value.length > 0) {
        filteredOptionsList = filteredOptionsList.filter(
          (option) => !disabledOptionIds.value.includes(option.value.id)
        )
      }

      const addedOptionsList = alreadyAddedOptions.value || []

      return [...filteredOptions, ...filteredOptionsList, ...addedOptionsList]
    })

    const showLoadingLabel = computed(() => {
      return !isLastPage.value && !props.externalOptions
    })

    const checkForCloseSelect = computed(() => {
      return props.isMultiple ? false : props.closeAfterSelect
    })

    const alreadyAddedOptions = computed(() => {
      return props?.addedOptions || []
    })

    const disabledOptionIds = computed(() => {
      return alreadyAddedOptions.value?.map((el) => el?.value?.id) || []
    })

    watch(
      () => props.selected,
      (newValue, oldValue) => {
        if (
          newValue !== oldValue &&
          newValue !== null &&
          newValue !== undefined
        ) {
          state.selectedValue = newValue
        }
      }
    )

    const instance = getCurrentInstance()
    const uuid = ref(instance.uid)
    const resolvedInputId = computed(
      () => props.inputId || `lazyLoadSelect-${uuid.value}`
    )

    const lazyLoadSelectRootRef = ref(null)
    const lazyLoadCustomSelectRef = ref(null)
    let detachLazyDropdownScroll = null

    const removeLazyDropdownScrollListener = () => {
      if (typeof detachLazyDropdownScroll === "function") {
        detachLazyDropdownScroll()
      }
      detachLazyDropdownScroll = null
    }

    onMounted(() => {
      state.selectedValue =
        props.default || props.selected || state.selectedValue
      // Emit default value value to update model if set
      if (props.default) {
        emit("changed", state.selectedValue)
      }
    })

    onUnmounted(() => {
      removeLazyDropdownScrollListener()
    })

    const isOptionSelectable = (option) => {
      return !disabledOptionIds.value?.includes(option?.value?.id)
    }

    const onSearch = (query, loading) => {
      const searchQuery = query.toString()
      if (
        searchQuery.replace(/\s/g, "").length > 1 ||
        searchQuery.replace(/\s/g, "").length === 0
      ) {
        clearTimeout(state.processingTimeOut)
        state.processingTimeOut = setTimeout(() => {
          resetParams()
          state.searchQuery = searchQuery ? searchQuery : ""
          getOptions(state.params, loading)
        }, 600)
      }
    }

    const onChange = (value) => {
      emit("changed", value)
      emit("update:model-value", value)
    }

    const loadMore = () => {
      if (state.processingTimeOut) {
        clearTimeout(state.processingTimeOut)
      }
      state.processingTimeOut = setTimeout(() => {
        state.params.page++
        getOptions(state.params, false, true)
      }, 100)
    }

    const handleDropdownMenuScroll = (event) => {
      const menu = event.target
      if (!menu || typeof menu.scrollTop !== "number") {
        return
      }
      if (!menu.classList?.contains?.("vs__dropdown-menu")) {
        return
      }
      if (!state.options?.length || isLastPage.value) {
        return
      }
      if (menu.offsetHeight + menu.scrollTop + 8 >= menu.scrollHeight) {
        loadMore()
      }
    }

    const attachLazyDropdownScrollListener = () => {
      removeLazyDropdownScrollListener()
      if (props.appendDropdownToBody) {
        nextTick(() => {
          requestAnimationFrame(() => {
            const menu =
              lazyLoadCustomSelectRef.value?.getDropdownMenuElement?.() || null
            if (menu?.addEventListener) {
              menu.addEventListener("scroll", handleDropdownMenuScroll, {
                passive: true
              })
              detachLazyDropdownScroll = () => {
                menu.removeEventListener("scroll", handleDropdownMenuScroll)
              }
            }
          })
        })
      } else {
        const root = lazyLoadSelectRootRef.value
        if (root?.addEventListener) {
          root.addEventListener("scroll", handleDropdownMenuScroll, {
            capture: true,
            passive: true
          })
          detachLazyDropdownScroll = () => {
            root.removeEventListener("scroll", handleDropdownMenuScroll, {
              capture: true
            })
          }
        }
      }
    }

    const onOpenSelect = () => {
      if (
        state.params &&
        state.params.page > 1 &&
        (!state.selectedValue ||
          (Array.isArray(state.selectedValue) &&
            state.selectedValue.length === 0))
      ) {
        resetParams()
      }
      if (!props.disabled) {
        getOptions(state.params)
      }
      if (props.refreshOnOpen) {
        refreshSelect()
      }
      state.isOpened = true
      attachLazyDropdownScrollListener()
    }

    const onCloseSelect = () => {
      removeLazyDropdownScrollListener()
      state.isOpened = false
    }

    const resetParams = () => {
      state.params = {
        per_page: 25,
        page: 1
      }
    }

    const finalizeLoadingState = (loading) => {
      if (typeof loading === "function") {
        loading(false)
      }
      state.isProcessing = false
    }

    const getOptions = (params, loading, isLoadMore) => {
      if (!props.externalOptions) {
        state.isProcessing = true
        let search_query = ""
        const newParams = { ...params, type: props.type }

        if (state.searchQuery && state.searchKeys[props.type]) {
          state.searchKeys[props.type].forEach((key) => {
            search_query = search_query + `${key}:"${state.searchQuery}", `
          })
          if (state.searchKeys[props.type].includes("email")) {
            newParams.operator = "OR"
          }
        }
        if (props.showInactive) {
          newParams.active_only = false
        } else {
          newParams.active_only = true
        }
        if (search_query) {
          newParams.search_query = search_query.slice(0, -2)
        } else {
          newParams.search_query = ""
        }
        if (props.type === "appointment") {
          newParams.only_allowed_apt =
            props.field && props.field === "departing_from" ? false : true
        }

        if (props.adultPassRequestDisabled) {
          newParams.adultPassRequestDisabled = true
        }

        if (props.isSLP) {
          newParams.is_slp = true
        }

        if (props.context) {
          newParams.context = props.context
        }
        if (typeof loading === "function") {
          loading(true)
        }
        if (props.reports) {
          newParams.reports = true
        }

        if (props.context) {
          newParams.context = props.context
        }

        const action = props.forceClearCache
          ? `${props.isInKiosk ? "kiosks" : "search"}/searchByTypeAndQueryAxios`
          : `${props.isInKiosk ? "kiosks" : "search"}/searchByTypeAndQuery`

        let hasFinalizedLoadingState = false
        const finalizeLoadingStateOnce = () => {
          if (hasFinalizedLoadingState) {
            return
          }
          hasFinalizedLoadingState = true
          finalizeLoadingState(loading)
        }

        store
          .dispatch(action, newParams)
          .then((response) => {
            setData(response, isLoadMore)
          })
          .catch(() => {
            finalizeLoadingStateOnce()
          })
          .finally(() => {
            finalizeLoadingStateOnce()
          })
      }
    }

    const setData = (response, isLoadMore) => {
      const data = response.data.data
      const meta = response.data.meta
      if (data) {
        if (!isLoadMore && !props.externalOptions) {
          state.options = []
        }
        if (Array.isArray(data)) {
          pushOptions(data)
        } else {
          for (const key in data) {
            if (Object.hasOwnProperty.call(data, key)) {
              const element = data[key]
              if (element && element.data) {
                pushOptions(element.data)
              }
            }
          }
        }
      }
      if (meta) {
        state.last_page = meta.last_page
        state.total = meta.total
      }
    }

    const pushOptions = (data) => {
      data.forEach((option) => {
        if (!option.role_id) {
          if (props.onlyBasicData) {
            state.options.push({
              value: {
                id: option.id,
                type: "App\\Models\\Room"
              },
              label: option.name
            })
          } else {
            state.options.push({
              value: {
                id: option.id,
                type: "App\\Models\\Room",
                comment_type: option.comment_type,
                unavailability: Object.prototype.hasOwnProperty.call(
                  option,
                  "active_unavailability"
                )
                  ? option.active_unavailability
                  : null
              },
              label: option.name,
              capacity: option.capacity ? option.capacity : null,
              owner_pin: option.owner_pin && option.owner_pin.pin,
              restriction: option.restriction ? option.restriction : null
            })
          }
        } else {
          state.options.push({
            value: {
              id: option.id,
              type: "App\\Models\\User",
              passesToday: option.passes_for_today_count,
              unavailability: Object.prototype.hasOwnProperty.call(
                option,
                "active_unavailability"
              )
                ? option.active_unavailability
                : null
            },
            label: option.email
              ? option.first_name + " " + option.last_name + ", " + option.email
              : option.first_name + " " + option.last_name
          })
        }
        state.options.sort((a, b) => {
          return b.value.type.localeCompare(a.value.type)
        })
      })
    }

    const refreshSelect = () => {
      getOptions(state.params, false)
    }

    return {
      uuid,
      resolvedInputId,
      lazyLoadSelectRootRef,
      lazyLoadCustomSelectRef,
      state,
      selectOptions,
      showLoadingLabel,
      isLastPage,
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect,
      resetParams,
      getOptions,
      setData,
      pushOptions,
      refreshSelect,
      checkForCloseSelect,
      isOptionSelectable,
      autoscrollEnabled
    }
  }
}
</script>
